/**
 * Service worker registration with strict guards + update notification.
 *
 *  - Never runs in iframes (Lovable editor preview is iframed)
 *  - Never runs in dev mode or on Lovable preview/sandbox hosts
 *  - Runs on the published .lovable.app domain and on custom domains
 *  - Emits `pwa:update-available` when a new SW is waiting
 *  - Exposes `window.__applyPwaUpdate()` and `window.__forcePwaReset()`
 */

const UPDATE_EVENT = "pwa:update-available";

declare global {
  interface Window {
    __applyPwaUpdate?: () => Promise<void>;
    __forcePwaReset?: () => Promise<void>;
  }
}

const shouldSkipRegistration = () => {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return true;
  // Iframe (Lovable editor)
  try {
    if (window.self !== window.top) return true;
  } catch {
    return true;
  }
  const host = window.location.hostname;
  // Only block actual Lovable preview/dev sandboxes — NOT published .lovable.app
  if (
    host.startsWith("id-preview--") ||
    host.startsWith("preview--") ||
    host === "lovableproject.com" ||
    host.endsWith(".lovableproject.com") ||
    host === "lovableproject-dev.com" ||
    host.endsWith(".lovableproject-dev.com") ||
    host === "beta.lovable.dev" ||
    host.endsWith(".beta.lovable.dev") ||
    host === "localhost" ||
    host === "127.0.0.1"
  ) {
    return true;
  }
  if (import.meta.env.DEV) return true;
  // Manual kill-switch: ?sw=off
  if (new URLSearchParams(window.location.search).get("sw") === "off") return true;
  return false;
};

const unregisterAll = async () => {
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(regs.map((r) => r.unregister()));
  } catch {
    /* noop */
  }
};

const clearAllCaches = async () => {
  try {
    if (typeof caches === "undefined") return;
    const keys = await caches.keys();
    await Promise.all(keys.map((k) => caches.delete(k)));
  } catch {
    /* noop */
  }
};

export const registerPWA = async () => {
  // Always expose a "nuke and reload" helper for admin debug, even when SW
  // registration is skipped — useful to clear stale browser caches manually.
  window.__forcePwaReset = async () => {
    await unregisterAll();
    await clearAllCaches();
    window.location.reload();
  };

  if (shouldSkipRegistration()) {
    // Clean up any service workers that may have been registered previously
    await unregisterAll();
    return;
  }

  try {
    const { registerSW } = await import("virtual:pwa-register");

    // User must explicitly opt-in to the reload (via PwaUpdatePrompt) — we
    // never auto-reload when the tab regains focus, otherwise switching apps
    // or browser tabs would kick the user off the page they were viewing.
    let userTriggeredUpdate = false;

    const updateSW = registerSW({
      immediate: true,
      onNeedRefresh() {
        // A new SW is installed and waiting. Notify the app to show a prompt.
        window.dispatchEvent(new CustomEvent(UPDATE_EVENT));
      },
      onOfflineReady() {
        /* could surface "ready to work offline" toast here */
      },
      onRegisteredSW(_swUrl, registration) {
        if (!registration) return;
        // Background update check every 30 min while the app is open.
        // We intentionally do NOT listen to focus/visibilitychange/online:
        // otherwise every tab switch would trigger an update + reload and
        // yank the user off the current screen.
        setInterval(() => {
          registration.update().catch(() => {});
        }, 30 * 60 * 1000);
      },
    });

    // Reload only when the user explicitly accepted the update prompt.
    let reloading = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (reloading || !userTriggeredUpdate) return;
      reloading = true;
      window.location.reload();
    });

    window.__applyPwaUpdate = async () => {
      userTriggeredUpdate = true;
      // Triggers skipWaiting() inside the SW -> controllerchange -> reload.
      await updateSW(true);
    };
  } catch {
    /* virtual module unavailable in this build */
  }
};
